<template>
  <div class="w-full flex flex-col overflow-hidden min-h-screen bg-gray-900 text-white">
    <loading v-if='loading' />

    <pbc-error v-if='!!error' :error='error' :retry='true' @retry='onRetry' message='Could not load template.' />

    <!-- Slot-fill form: shown when the template has named slots -->
    <div v-if='!loading && !error && slots.length > 0' class="flex flex-col w-full max-w-lg mx-auto p-4 gap-4">

      <!-- Header -->
      <div class="flex flex-col gap-2 pt-4">
        <img v-if='thumbnailUrl' :src='thumbnailUrl' class="w-full rounded-lg object-contain max-h-48" />
        <h1 class="text-xl font-bold text-center">{{ templateTitle }}</h1>
      </div>

      <!-- Slot fields -->
      <div v-for='slot in slots' :key='slot.id' class="flex flex-col gap-1">
        <label class="text-sm font-semibold text-gray-300">
          {{ slot.label }}<span v-if='slot.required' class="text-red-400 ml-1">*</span>
        </label>
        <textarea
          v-model='slotValues[slot.id]'
          :placeholder='slot.hint || slot.label'
          rows='2'
          class="w-full bg-gray-800 border border-gray-600 rounded-lg p-3 text-white text-base resize-none focus:outline-none focus:border-blue-500"
        />
      </div>

      <!-- Title field -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-semibold text-gray-300">Title</label>
        <input
          v-model='postTitle'
          type='text'
          placeholder='Give your post a title...'
          class="w-full bg-gray-800 border border-gray-600 rounded-lg p-3 text-white text-base focus:outline-none focus:border-blue-500"
        />
      </div>

      <!-- Error message -->
      <p v-if='publishError' class="text-red-400 text-sm text-center">{{ publishError }}</p>

      <!-- Actions -->
      <div class="flex gap-3 pb-8">
        <button
          @click='openInEditor'
          class="flex-1 py-3 rounded-lg bg-gray-700 text-gray-200 font-semibold text-base"
        >
          Open in editor
        </button>
        <button
          @click='publish'
          :disabled='publishing || !canPublish'
          class="flex-1 py-3 rounded-lg font-semibold text-base"
          :class='canPublish && !publishing ? "bg-blue-600 text-white" : "bg-gray-600 text-gray-400 cursor-not-allowed"'
        >
          <span v-if='publishing'>Publishing…</span>
          <span v-else>Publish →</span>
        </button>
      </div>
    </div>

    <!-- No slots: redirect immediately to editor (existing behaviour) -->
    <!-- This div is empty; redirect happens in the apollo result handler -->
  </div>
</template>

<script>
import { savePostDraftQuery, getTemplateQuery } from '@/gql/Queries.js';
import { format, formatISO9075, addMinutes } from 'date-fns';
import gql from 'graphql-tag';

const LONG_PATTERN = "yyyy-MM-dd'T'HH:mm:ss";

export default {
  inject: ['api'],

  data() {
    return {
      loading: true,
      error: '',
      templateTitle: '',
      thumbnailUrl: '',
      slots: [],         // [{ id, label, hint, required }]
      slotValues: {},    // { slotId: value }
      postTitle: '',
      rawGamedata: null, // the full parsed gamedata object
      publishing: false,
      publishError: '',
    };
  },

  apollo: {
    getTemplate: {
      query: getTemplateQuery,
      variables() {
        return { id: this.templateId };
      },
      error(error) {
        this.error = error.message;
        this.loading = false;
      },
      async result({ data, loading }) {
        if (loading || !data?.getTemplate) return;

        const t = data.getTemplate;
        const gamedata = typeof t.data === 'string' ? JSON.parse(t.data) : t.data;

        this.templateTitle = t.title;
        this.thumbnailUrl = t.thumbnailImage ? this.api.makeImageUrlEditor(t.thumbnailImage) : '';
        this.rawGamedata = gamedata;
        this.postTitle = t.title;

        // Extract slots from template.components
        const templateComponents = gamedata?.template?.components || [];
        this.slots = templateComponents
          .filter(c => c.slot)
          .map(c => ({
            id:        c.slot.id,
            label:     c.slot.label,
            hint:      c.slot.hint || '',
            type:      c.slot.type || 'text',
            required:  c.slot.required !== false,
            component: c.component,
          }));

        // Initialise slot value map
        const values = {};
        this.slots.forEach(s => { values[s.id] = ''; });
        this.slotValues = values;

        this.loading = false;

        // No slots → fall through to the original editor flow
        if (this.slots.length === 0) {
          await this.saveAndOpenEditor(gamedata, t.title);
        }
      },
    },
  },

  computed: {
    templateId() {
      return this.$route.params.templateId;
    },
    canPublish() {
      return (
        this.postTitle.trim().length > 0 &&
        this.slots.filter(s => s.required).every(s => (this.slotValues[s.id] || '').trim().length > 0)
      );
    },
  },

  methods: {
    onRetry() {
      this.$apollo.queries.getTemplate.refetch();
      this.error = '';
      this.loading = true;
    },

    /** Apply current slot values to a deep clone of the gamedata */
    applySlots(gamedata) {
      const cloned = JSON.parse(JSON.stringify(gamedata));

      // Build component-id → slot-id map
      const componentToSlot = {};
      for (const comp of (cloned.template?.components || [])) {
        if (comp.slot) componentToSlot[comp.component] = comp.slot.id;
      }

      for (const scene of (cloned.scenes || [])) {
        for (const comp of (scene.components || [])) {
          const slotId = componentToSlot[comp.id];
          if (slotId && slotId in this.slotValues) {
            if (comp.type === 'text') comp.text = this.slotValues[slotId];
          }
        }
      }
      return cloned;
    },

    /** One-click publish: apply slots → signed S3 upload → createPost */
    async publish() {
      if (!this.canPublish || this.publishing) return;
      this.publishing = true;
      this.publishError = '';

      try {
        const gamedata = this.applySlots(this.rawGamedata);

        // Step 1: get a signed upload URL + game UUID from AppSync
        const signedResp = await this.$apollo.mutate({
          fetchPolicy: 'no-cache',
          mutation: gql`mutation { getSignedGameUploadUrl { id url } }`,
        });
        const { id: gameId, url: signedUrl } = signedResp.data.getSignedGameUploadUrl;
        gamedata.id = gameId;

        // Step 2: upload gamedata JSON to S3 via the signed URL
        await fetch(signedUrl, {
          method: 'PUT',
          body: JSON.stringify(gamedata),
          headers: { 'Content-Type': 'application/json' },
        });

        // Step 3: create the post
        const postResp = await this.$apollo.mutate({
          mutation: gql`
            mutation createPost($parent: String!, $op: String!, $title: String!, $game: String!) {
              createPost(parent: $parent, op: $op, title: $title, game: $game) {
                id title game author { id username }
              }
            }
          `,
          variables: {
            parent: 'root',
            op: 'root',
            title: this.postTitle,
            game: gameId,
          },
        });

        const post = postResp.data.createPost;
        this.$ana.track({ eventCategory: 'template', eventAction: 'publishFromSlots', extra: { templateId: this.templateId, postId: post.id } });

        // Redirect to the feed
        this.$router.replace({ name: 'hot' });

      } catch (err) {
        this.publishError = err.message || 'Something went wrong. Please try again.';
        this.publishing = false;
      }
    },

    /** Open in the full editor (apply slots to the draft first if any are filled) */
    async openInEditor() {
      const gamedata = Object.values(this.slotValues).some(v => v.trim().length > 0)
        ? this.applySlots(this.rawGamedata)
        : this.rawGamedata;
      await this.saveAndOpenEditor(gamedata, this.postTitle);
    },

    /** Existing flow: save as a local draft and redirect to the create editor */
    async saveAndOpenEditor(gamedata, title) {
      const created = format(this.getUTCDate(), LONG_PATTERN);
      const updated = format(this.getUTCDate(), LONG_PATTERN);
      const draftId = this.$route.params.draftId || formatISO9075(this.getUTCDate(), { format: 'basic' }).replace(' ', '-');

      await this.$apollo.mutate({
        mutation: savePostDraftQuery,
        fetchPolicy: 'no-cache',
        client: 'localClient',
        variables: { id: draftId, created, updated, title, gamedata: JSON.stringify(gamedata), parentId: 'root', opId: 'root' },
      });

      this.$router.replace({ name: 'create', params: { type: 'post', draftId } });
    },

    getUTCDate() {
      const date = new Date();
      return addMinutes(date, date.getTimezoneOffset());
    },
  },
};
</script>
